import React, { useMemo } from "react";
import Icon from "../../../components/AppIcon";
import {
  PRICING_BY_SLUG,
  getFromPrice,
  getPriceForDate,
} from "../../../data/pricing";

const RoomInfo = ({ room, starCount = 0, currentPrice = null }) => {
  if (!room) return null;

  // "from" cijena (minimalna sezonska cijena za 2 gosta)
  const { todayPrice, fromPrice } = useMemo(() => {
    const seasons = PRICING_BY_SLUG?.[room.slug] || [];

    return {
      todayPrice: getPriceForDate(seasons, new Date(), 2),
      fromPrice: getFromPrice(seasons, 2),
    };
  }, [room.slug]);

  const displayPrice = currentPrice ?? todayPrice ?? fromPrice ?? room?.price;

  return (
    <div className="space-y-8">
      {/* Title / Stars / Price */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
            {room.title}
          </h1>

          {/* Stars (optional) */}
          {starCount > 0 && (
            <div className="flex items-center gap-1 mb-2">
              {Array.from({ length: starCount }).map((_, i) => (
                <Icon
                  key={`star-${i}`}
                  name="Star"
                  size={18}
                  className="text-amber-500 fill-current"
                />
              ))}
            </div>
          )}

          <div className="flex items-center space-x-4 text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={16} />
              <span>{room.location}</span>
            </div>
          </div>
        </div>

        <div className="text-right">
          <div className="flex flex-col items-end">
            {room?.originalPrice &&
              displayPrice != null &&
              room?.originalPrice > displayPrice && (
                <span className="text-lg text-muted-foreground line-through">
                  €{room?.originalPrice}
                </span>
              )}

            <span className="font-heading text-3xl font-bold text-primary">
              €{displayPrice}
            </span>

            <span className="text-sm text-muted-foreground">
              {currentPrice != null || todayPrice != null
                ? "per night"
                : "from / night"}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 bg-card rounded-xl border">
        <div className="text-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full mx-auto mb-2">
            <Icon name="Users" size={20} className="text-primary" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {room.capacity}
          </p>
          <p className="text-xs text-muted-foreground">Guests</p>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full mx-auto mb-2">
            <Icon name="Bed" size={20} className="text-primary" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {room.bedrooms}
          </p>
          <p className="text-xs text-muted-foreground">Bedroom</p>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full mx-auto mb-2">
            <Icon name="Bath" size={20} className="text-primary" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {room.bathrooms}
          </p>
          <p className="text-xs text-muted-foreground">Bathroom</p>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full mx-auto mb-2">
            <Icon name="Home" size={20} className="text-primary" />
          </div>
          <p className="text-lg font-semibold text-foreground">{room.size}m²</p>
          <p className="text-xs text-muted-foreground">Size</p>
        </div>
      </div>

      {/* Description */}
      {room.description && (
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
            Description
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {room.description}
          </p>
        </div>
      )}

      {/* Key Features */}
      {Array.isArray(room.keyFeatures) && room.keyFeatures.length > 0 && (
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
            Key Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {room.keyFeatures.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                <span className="text-muted-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Amenities */}
      {Array.isArray(room.amenities) && room.amenities.length > 0 && (
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
            Amenities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {room.amenities.map((amenity, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-4 bg-card rounded-lg border"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={amenity.icon}
                    size={20}
                    className="text-primary"
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {amenity.label}
                  </h3>
                  {amenity.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {amenity.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Location & Check-in Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl p-6 border">
          <h3 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center">
            <Icon name="MapPin" size={20} className="mr-2 text-primary" />
            Location
          </h3>
          <p className="text-muted-foreground mb-2">{room.address}</p>
          <p className="text-sm text-muted-foreground">
            Located {room.location}, within walking distance of major
            attractions and amenities.
          </p>
        </div>

        <div className="bg-card rounded-xl p-6 border">
          <h3 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Clock" size={20} className="mr-2 text-primary" />
            Check-in Details
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-in:</span>
              <span className="text-foreground font-medium">
                {room?.availability?.checkin}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-out:</span>
              <span className="text-foreground font-medium">
                {room?.availability?.checkout}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Minimum stay:</span>
              <span className="text-foreground font-medium">
                {room?.availability?.minStay} nights
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomInfo;
